import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import { useInAppEscape } from "@/lib/inAppEscape/InAppEscapeContext";
import { buildAppIntentUrl } from "./appIntent";
import { buildExternalBrowserUrl } from "./externalBrowser";

type UseWebViewAppOpenParams = {
  packageName: string | null | undefined;
  createClick: () => Promise<string | null>;
  playStoreUrlWithClickId: (clickId: string) => string | null;
};

export type WebViewAppOpen = {
  onStoreTap: (event: MouseEvent<HTMLAnchorElement>) => void;
};

type AppOpenAttempt = { os: "ios" | "android"; url: string };

function buildAppOpenAttempt(
  packageName: string | null | undefined
): AppOpenAttempt | null {
  if (typeof window === "undefined") return null;
  const pageUrl = window.location.href;
  const userAgent = navigator.userAgent;

  const intentUrl = buildAppIntentUrl(pageUrl, userAgent, packageName);
  if (intentUrl) return { os: "android", url: intentUrl };

  const externalBrowserUrl = buildExternalBrowserUrl(pageUrl, userAgent);
  if (externalBrowserUrl) return { os: "ios", url: externalBrowserUrl };

  return null;
}

/**
 * Tries to open the installed app from Meta's in-app browsers. See
 * buildAppIntentUrl and buildExternalBrowserUrl.
 *
 * When it tries, the page does not redirect and does not record a click on
 * load. Meta's prompt can stay open for a long time, and an app that opens
 * records the click itself. The store button records it instead.
 *
 * This changes what a click means for flagged links. A visitor who leaves
 * without opening the app or tapping the store is not counted. One who comes
 * back from Safari and taps the store here is counted twice.
 *
 * Returns null when there is nothing to try, so the page works as before.
 */
export function useWebViewAppOpen({
  packageName,
  createClick,
  playStoreUrlWithClickId,
}: UseWebViewAppOpenParams): WebViewAppOpen | null {
  // Runs only on the client. Callers read it after OS detection, which never
  // happens on the first render, so the server HTML still matches.
  const [attempt] = useState(() => buildAppOpenAttempt(packageName));
  const pendingClickId = useRef<Promise<string | null> | null>(null);
  const inAppEscape = useInAppEscape();

  useEffect(() => {
    if (!attempt) return;
    try {
      window.location.href = attempt.url;
    } catch {
      // Some WebViews throw on unknown schemes. The store button still works.
    }
  }, [attempt]);

  const onStoreTap = useCallback(
    async (event: MouseEvent<HTMLAnchorElement>) => {
      const storeUrl = event.currentTarget.href;
      event.preventDefault();

      // Reuse the click from an earlier tap, so one visit counts once.
      pendingClickId.current ??= createClick();
      const clickId = await pendingClickId.current;

      // Only the Play Store can pass the click id to the installed app.
      const destination =
        (attempt?.os === "android" &&
          clickId &&
          playStoreUrlWithClickId(clickId)) ||
        storeUrl;

      // Instagram on iOS blocks App Store links, so go through the escape. No
      // fromTap: after the await the tap no longer counts, and Instagram's
      // escape works without one.
      if (inAppEscape?.start(destination, { clickId })) return;
      window.location.href = destination;
    },
    [attempt, createClick, playStoreUrlWithClickId, inAppEscape]
  );

  return useMemo(
    () => (attempt ? { onStoreTap } : null),
    [attempt, onStoreTap]
  );
}
